import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/app';
import { cn } from '../../../lib/utils';
import { SettlementDialogProps } from '../types';

export function SettlementDialog({
  isOpen,
  onOpenChange,
  providers,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isSubmitting,
  isBankConfirmed,
}: SettlementDialogProps) {
  const provider = useMemo(
    () => providers.find((p) => p.id === formData.providerId),
    [providers, formData.providerId],
  );
  const currency = provider?.currency || 'UZS';
  const needsRate = currency !== 'UZS';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0">
        <DialogHeader bordered>
          <DialogTitle>{isEditing ? 'Изменить поступление' : 'Поступление вручную'}</DialogTitle>
          <DialogDescription>
            {isBankConfirmed
              ? 'Подтверждено банком: сумму, дату и провайдера менять нельзя, только комментарий. Ошибку исправляют отдельным Поступлением с обратным знаком.'
              : 'Деньги, реально зачисленные на счёт по отчёту провайдера. Возврат заводится отрицательной суммой.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Дата зачисления" required>
              <Input
                type="date"
                value={formData.date}
                disabled={isBankConfirmed}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
              />
            </FormField>
            <FormField label="Провайдер" required>
              {(p) => (
                <Select
                  value={formData.providerId}
                  disabled={isBankConfirmed}
                  onValueChange={(value) => onFormDataChange((prev) => ({ ...prev, providerId: value }))}
                >
                  <SelectTrigger {...p}>
                    <SelectValue placeholder="Выберите провайдера" />
                  </SelectTrigger>
                  <SelectContent>
                    {providers
                      .filter((item) => item.is_active)
                      .map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          </div>

          <div className={cn('grid gap-4', needsRate && 'sm:grid-cols-2')}>
            <FormField label={`Сумма, ${currency}`} required hint="Отрицательная — возврат">
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={formData.amount || ''}
                disabled={isBankConfirmed}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              />
            </FormField>
            {needsRate && (
              <FormField label={`Курс ${currency} → сум`} required hint="Сумов за 1 единицу, после сохранения не пересчитывается">
                <Input
                  type="number"
                  step="0.0001"
                  inputMode="decimal"
                  value={formData.rate || ''}
                  disabled={isBankConfirmed}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                />
              </FormField>
            )}
          </div>

          <FormField label="Комментарий">
            <Input
              type="text"
              placeholder="Например: платёжка №123 за 11–17 августа"
              value={formData.comment || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))}
            />
          </FormField>
        </DialogBody>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
